import React, { useEffect, useState } from 'react';
import withAuth from '../../../hocs/withAuth';
import { Row, Col, Container, Badge } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchGroupDeals } from '../../../store/actions/deals';
import { BadgePercent, CalendarDays, Building2, Users } from 'lucide-react';

import NavbarBusiness from '../../Components/BusinessNavbar';
import BusinessSidebar from '../../Components/BusinessSidebar';
import CreateDealModal from '../CreateDealModal';

import './index.css';

const formatDate = (date) => {
	if (!date) return 'No date';
	return new Date(date).toLocaleDateString('en-CA', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
};

const getStatusClass = (status) => {
	switch (status) {
		case 'published':
			return 'deal-card__status deal-card__status--published';
		case 'draft':
			return 'deal-card__status deal-card__status--draft';
		case 'confirmed':
			return 'deal-card__status deal-card__status--confirmed';
		case 'full':
			return 'deal-card__status deal-card__status--full';
		case 'cancelled':
			return 'deal-card__status deal-card__status--cancelled';
		case 'expired':
			return 'deal-card__status deal-card__status--expired';
		case 'completed':
			return 'deal-card__status deal-card__status--completed';
		default:
			return 'deal-card__status';
	}
};

const DealsPage = () => {
	const dispatch = useDispatch();
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const dealsState = useSelector((state) => state.groupDeals);
	const deals = dealsState?.list || [];
	const { id } = useSelector((state) => state.currentUser.user);

	const toggleCreateModal = () => setIsCreateOpen((prev) => !prev);

	useEffect(() => {
		if (id) {
			dispatch(fetchGroupDeals(id));
		}
	}, [dispatch, id]);

	return (
		<div className='business-page-shell'>
			<NavbarBusiness />

			<Row className='g-0 no-gutters'>
				<Col xs='12' md='3' lg='3'>
					<BusinessSidebar />
				</Col>

				<Col xs='12' md='9' lg='9' className='business-page-shell__main'>
					<Container fluid className='deals-page'>
						<div className='deals-page__header'>
							<div>
								<h1 className='deals-page__title'>Group Deals</h1>
								<p className='deals-page__subtitle'>
									Create and manage building-specific offers for local
									residents.
								</p>
							</div>

							<div className='deals-page__actions'>
								<button
									type='button'
									className='deals-page__create-btn'
									onClick={toggleCreateModal}>
									+ Create Deal
								</button>
							</div>
						</div>

						<div className='deals-page__summary'>
							<div className='deals-page__summary-card'>
								<div className='deals-page__summary-icon'>
									<BadgePercent size={20} />
								</div>
								<div>
									<div className='deals-page__summary-value'>
										{deals.length}
									</div>
									<div className='deals-page__summary-label'>Total deals</div>
								</div>
							</div>
						</div>

						{deals.length > 0 ? (
							<Row>
								{deals.map((deal) => {
									const firstBuilding = deal?.targetBuildings?.[0]?.building;

									return (
										<Col xl='6' lg='6' md='12' key={deal._id}>
											<div className='deal-card'>
												<div className='deal-card__top'>
													<div className='deal-card__badge-wrap'>
														<span className={getStatusClass(deal.status)}>
															{deal.status || 'draft'}
														</span>
													</div>

													<div className='deal-card__price'>
														{deal.discountedPrice
															? `CD$ ${deal.discountedPrice}`
															: 'Special price'}
													</div>
												</div>

												<h3 className='deal-card__title'>
													{deal.title || 'Untitled deal'}
												</h3>

												<p className='deal-card__description'>
													{deal.description || 'No description available.'}
												</p>

												<div className='deal-card__meta-list'>
													<div className='deal-card__meta-item'>
														<CalendarDays size={16} />
														<span>Service: {formatDate(deal.serviceDate)}</span>
													</div>

													<div className='deal-card__meta-item'>
														<CalendarDays size={16} />
														<span>
															Deadline: {formatDate(deal.bookingDeadline)}
														</span>
													</div>

													<div className='deal-card__meta-item'>
														<Users size={16} />
														<span>
															{deal.bookedCount || 0} / {deal.maxBookings || 0}{' '}
															booked
														</span>
													</div>

													<div className='deal-card__meta-item'>
														<Building2 size={16} />
														<span>
															{firstBuilding?.address ||
																'Target building selected'}
														</span>
													</div>
												</div>

												<div className='deal-card__footer'>
													<div className='deal-card__footer-left'>
														{deal.originalPrice && deal.discountedPrice ? (
															<Badge className='deal-card__discount-badge'>
																{Math.round(
																	((deal.originalPrice - deal.discountedPrice) /
																		deal.originalPrice) *
																		100,
																)}
																% OFF
															</Badge>
														) : null}
													</div>

													<Link
														to={`/deals/${deal._id}`}
														className='deal-card__link'>
														View details
													</Link>
												</div>
											</div>
										</Col>
									);
								})}
							</Row>
						) : (
							<div className='deals-page__empty'>
								<div className='deals-page__empty-icon'>
									<BadgePercent size={28} />
								</div>
								<h3>No group deals yet</h3>
								<p>
									Create your first group deal and start marketing exclusive
									offers to attached buildings.
								</p>
								<div className='deals-page__empty-action'>
									<button
										type='button'
										className='deals-page__create-btn'
										onClick={toggleCreateModal}>
										+ Create Deal
									</button>
								</div>
							</div>
						)}

						<CreateDealModal isOpen={isCreateOpen} toggle={toggleCreateModal} />
					</Container>
				</Col>
			</Row>
		</div>
	);
};

export default withAuth(DealsPage);
